import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "sonner";
import Navbar from "@/components/Navbar";
import Features from "./pages/Features";
import PricingEn from "@/pages/PricingEn";
import Welcome from "@/pages/Welcome";
import AlphabetModules from "@/pages/AlphabetModules";

import Auth from "@/pages/Auth";
import AuthEn from "@/pages/AuthEn";
import GoogleCallback from "@/pages/GoogleCallback";
import { useEffect } from "react";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Roadmap from "@/pages/Roadmap";
import Learn from "@/pages/Learn";
import LearnCenter from "@/pages/LearnCenter";
import LessonPage from "@/pages/LessonPage";
import Practice from "@/pages/Practice";
import PublicIsland from "@/pages/PublicIsland";
import PublicIslandEn from "@/pages/PublicIslandEn";
import PracticeAlphabet from "@/pages/PracticeAlphabet";
import PracticeConjugation from "@/pages/PracticeConjugation";
import PracticeRoots from "@/pages/PracticeRoots";
import PracticeSentences from "@/pages/PracticeSentences";
import Reading from "@/pages/Reading";
import ReadingDetail from "@/pages/ReadingDetail";
import Cheatsheets from "@/pages/Cheatsheets";
import Progress from "@/pages/Progress";
import Exam from "@/pages/Exam";
import Account from "@/pages/Account";
import Admin from "@/pages/Admin";
import Review from "@/pages/Review";
import ModuleTest from "@/pages/ModuleTest";
import Landing from "@/pages/Landing";
import LandingEn from "@/pages/LandingEn";
import Dictionary from "@/pages/Dictionary";
import DictionaryWord from "@/pages/DictionaryWord";
import DictionaryEn from "@/pages/DictionaryEn";
import DictionaryWordEn from "@/pages/DictionaryWordEn";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import DailyPlanSession from "@/pages/DailyPlanSession";
import CourseQuestions from "@/pages/CourseQuestions";
import VocabQuizSession from "./pages/VocabQuizSession";
import VocabGroupWords from "@/pages/VocabGroupWords";
import MyErrors from "./pages/MyErrors";
import Rewards from "./pages/Rewards";
import Billing from "./pages/Billing";
import BillingEn from "./pages/BillingEn";
import Affiliate from "./pages/Affiliate";
import AffiliateProgram from "./pages/AffiliateProgram";
import CookieConsent from "@/components/CookieConsent";
import AdminSupportMessages from "@/pages/AdminSupportMessages";
import {
  LegalMentions,
  LegalCgu,
  LegalCgv,
  LegalPrivacy,
  LegalCookies,
  LegalWithdrawal,
} from "@/pages/Legal";
import Pricing from "@/pages/Pricing";
import PublicAlphabet from "@/pages/PublicAlphabet";
import PublicCheatsheets from "@/pages/PublicCheatsheets";

function Shell({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  const locked =
    user &&
    user.account_type === "expired" &&
    !["/pricing", "/affiliate-program", "/affiliate"].includes(loc.pathname) &&
    user.role !== "admin";
  return (
    <div className="min-h-screen relative">
      <Navbar />
      <main
        className={`max-w-7xl mx-auto px-4 pb-24 md:pb-16 ${locked ? "grayscale opacity-50 select-none" : ""}`}
        style={{ paddingTop: "calc(6rem + var(--trial-banner-h, 0px))" }}
      >
        {children}
      </main>
      {locked && (
        <div className="absolute inset-x-0 top-16 bottom-0 z-30 flex items-start justify-center bg-background/35 p-6 pt-24 backdrop-blur-[1px]">
          <div className="max-w-md rounded-3xl border bg-card p-7 text-center shadow-2xl">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-2xl">
              🔒
            </div>
            <h2 className="font-head text-2xl font-bold mb-2">
              Ton essai gratuit est terminé
            </h2>
            <p className="text-sm text-muted-foreground mb-5">
              Le site reste accessible, mais les leçons, exercices et l’île sont
              en pause. Choisis une formule ou demande un accès gratuit grâce à
              tes vidéos.
            </p>
            <button
              onClick={() => {
                window.location.href = "/pricing";
              }}
              className="w-full rounded-xl bg-primary px-4 py-3 font-semibold text-primary-foreground hover:opacity-90"
            >
              Voir les formules et l’accès gratuit
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MaybeShell({ children }) {
  const { user } = useAuth();
  if (user && user.onboarded) {
    return <Shell>{children}</Shell>;
  }
  return children;
}

function Protected({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Chargement…
      </div>
    );
  if (user === false)
    return <Navigate to="/auth" replace state={{ from: loc.pathname }} />;
  if (!user.onboarded && loc.pathname !== "/welcome")
    return <Navigate to="/welcome" replace state={{ from: loc.pathname }} />;
  return children;
}

function LandingRoute() {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  if (user && user.onboarded) return <Navigate to="/dashboard" replace />;
  return <LandingEn />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => window.scrollTo(0, 0), [pathname]);
  return null;
}

function LandingRouteFr() {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Chargement…
      </div>
    );
  if (user && user.onboarded) return <Navigate to="/dashboard" replace />;
  return <Landing />;
}

function Routed() {
  return (
    <Routes>
      <Route path="/" element={<LandingRoute />} />
      <Route path="/fr" element={<LandingRouteFr />} />
      <Route
        path="/dictionary"
        element={
          <MaybeShell>
            <DictionaryEn />
          </MaybeShell>
        }
      />
      <Route
        path="/dictionary/:dialect/:slug"
        element={
          <MaybeShell>
            <DictionaryWordEn />
          </MaybeShell>
        }
      />
      <Route
        path="/fr/dictionary"
        element={
          <MaybeShell>
            <Dictionary />
          </MaybeShell>
        }
      />
      <Route
        path="/fr/dictionary/:dialect/:slug"
        element={
          <MaybeShell>
            <DictionaryWord />
          </MaybeShell>
        }
      />
      <Route
        path="/blog"
        element={
          <MaybeShell>
            <Blog lang="en" />
          </MaybeShell>
        }
      />
      <Route
        path="/blog/:slug"
        element={
          <MaybeShell>
            <BlogPost lang="en" />
          </MaybeShell>
        }
      />
      <Route
        path="/fr/blog"
        element={
          <MaybeShell>
            <Blog lang="fr" />
          </MaybeShell>
        }
      />
      <Route
        path="/fr/blog/:slug"
        element={
          <MaybeShell>
            <BlogPost lang="fr" />
          </MaybeShell>
        }
      />
      <Route
        path="/oasis/s/:token"
        element={
          <MaybeShell>
            <PublicIslandEn />
          </MaybeShell>
        }
      />
      <Route
        path="/fr/oasis/s/:token"
        element={
          <MaybeShell>
            <PublicIsland />
          </MaybeShell>
        }
      />
      <Route path="/auth" element={<AuthEn />} />
      <Route path="/fr/auth" element={<Auth />} />
      <Route path="/auth/google" element={<GoogleCallback />} />
      <Route path="/legal/mentions" element={<LegalMentions />} />
      <Route path="/legal/cgu" element={<LegalCgu />} />
      <Route path="/legal/cgv" element={<LegalCgv />} />
      <Route
        path="/pricing"
        element={
          <MaybeShell>
            <PricingEn />
          </MaybeShell>
        }
      />
      <Route
        path="/welcome"
        element={
          <Protected>
            <Welcome />
          </Protected>
        }
      />

      <Route path="/fr/tarifs" element={<Pricing />} />
      <Route path="/alphabet" element={<PublicAlphabet />} />
      <Route path="/fr/fiches" element={<PublicCheatsheets />} />
      <Route path="/features" element={<Features />} />

      <Route path="/legal/confidentialite" element={<LegalPrivacy />} />
      <Route path="/legal/cookies" element={<LegalCookies />} />
      <Route path="/legal/retractation" element={<LegalWithdrawal />} />
      <Route
        path="/onboarding"
        element={
          <Protected>
            <Onboarding />
          </Protected>
        }
      />
      <Route
        path="/dashboard"
        element={
          <Protected>
            <Shell>
              <Dashboard />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/daily-plan"
        element={
          <Protected>
            <Shell>
              <DailyPlanSession />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/roadmap"
        element={
          <Protected>
            <Shell>
              <Roadmap />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/vocab/groups/:groupId"
        element={
          <Protected>
            <Shell>
              <VocabGroupWords />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/learn"
        element={
          <Protected>
            <Shell>
              <LearnCenter />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/learn/:themeId"
        element={
          <Protected>
            <Shell>
              <Learn />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/learn/lesson/:lessonId"
        element={
          <Protected>
            <Shell>
              <LessonPage />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/practice"
        element={
          <Protected>
            <Shell>
              <Practice />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/practice/course-questions"
        element={
          <Protected>
            <Shell>
              <CourseQuestions />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/practice/alphabet"
        element={
          <Protected>
            <Shell>
              <AlphabetModules />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/practice/alphabet/:moduleSlug"
        element={
          <Protected>
            <Shell>
              <PracticeAlphabet />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/practice/conjugation"
        element={
          <Protected>
            <Shell>
              <PracticeConjugation />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/practice/sentences"
        element={
          <Protected>
            <Shell>
              <PracticeSentences />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/practice/roots"
        element={
          <Protected>
            <Shell>
              <PracticeRoots />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/practice/reading"
        element={
          <Protected>
            <Shell>
              <Reading />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/practice/reading/:rid"
        element={
          <Protected>
            <Shell>
              <ReadingDetail />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/cheatsheets"
        element={
          <Protected>
            <Shell>
              <Cheatsheets />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/progress"
        element={
          <Protected>
            <Shell>
              <Progress />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/exam/:level"
        element={
          <Protected>
            <Shell>
              <Exam />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/account"
        element={
          <Protected>
            <Shell>
              <Account />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/billing"
        element={
          <MaybeShell>
            <BillingEn />
          </MaybeShell>
        }
      />
      <Route
        path="/fr/billing"
        element={
          <Shell>
            <Billing />
          </Shell>
        }
      />
      <Route
        path="/affiliate"
        element={
          <Protected>
            <Shell>
              <Affiliate />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/affiliate-program"
        element={
          <Shell>
            <AffiliateProgram />
          </Shell>
        }
      />
      <Route
        path="/vocab-quiz"
        element={
          <Protected>
            <Shell>
              <VocabQuizSession />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/admin"
        element={
          <Protected>
            <Shell>
              <Admin />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/admin/support"
        element={
          <Protected>
            <Shell>
              <AdminSupportMessages />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/review"
        element={
          <Protected>
            <Shell>
              <Review />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/recompenses"
        element={
          <Protected>
            <Shell>
              <Rewards />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/module/:mid/test"
        element={
          <Protected>
            <Shell>
              <ModuleTest />
            </Shell>
          </Protected>
        }
      />
      <Route
        path="/my-errors"
        element={
          <Protected>
            <Shell>
              <MyErrors />
            </Shell>
          </Protected>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routed />
          <CookieConsent />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
